import { Box, Grid } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { END_POINTS } from "constants/APIConstant";
import fetch from "auth/FetchInterceptor";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import CircularProgress from "@mui/material/CircularProgress";
import CountdownTimer from "pages/forget-pass/components/count-down-timer";
import SnackbarModal from "component/shared/snackbar";
import ReactGA from "react-ga4";
import InnerHeader from "component/shared/innerheader/InnerHeader";
import ContentLoading from "component/LoadingComponent/ContentLoading";
import ResultComponent from "pages/Verification/components/ResultComponent";

const EMAIL_VERIFICATION_STORAGE_KEY = "emailVerificationCountDownTimer";
const DEFAULT_RESET_MINUTES = 10;

export default function VerifyEmailCode() {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const [verified, setVerified] = useState(null);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [message, setMessage] = useState(null);
  const [open, setOpen] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [code, setCode] = useState("");

  const verificationData =
    location.state?.verificationData ||
    (typeof window !== "undefined" &&
      (() => {
        try {
          const raw = sessionStorage.getItem("emailVerificationData");
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })());

  const userId = verificationData?.userId ?? null;
  const resetTimeFromResponse = verificationData?.resetTime ?? null;

  const getValidCountDate = () => {
    const storedTimer = localStorage.getItem(EMAIL_VERIFICATION_STORAGE_KEY);
    const now = Date.now();
    const tenMinutesFromNow = now + DEFAULT_RESET_MINUTES * 60 * 1000;
    const parsedResetTime = resetTimeFromResponse
      ? new Date(resetTimeFromResponse).getTime()
      : NaN;
    const endTime =
      !Number.isNaN(parsedResetTime) && parsedResetTime > now
        ? parsedResetTime
        : tenMinutesFromNow;

    if (!storedTimer) {
      const validDate = new Date(endTime).toISOString();
      localStorage.setItem(EMAIL_VERIFICATION_STORAGE_KEY, validDate);
      return validDate;
    }

    try {
      const storedDate = new Date(storedTimer);
      const storedTime = storedDate.getTime();
      if (isNaN(storedTime) || storedTime <= now) {
        const validDate = new Date(endTime).toISOString();
        localStorage.setItem(EMAIL_VERIFICATION_STORAGE_KEY, validDate);
        return validDate;
      }
      return storedTimer;
    } catch {
      const validDate = new Date(endTime).toISOString();
      localStorage.setItem(EMAIL_VERIFICATION_STORAGE_KEY, validDate);
      return validDate;
    }
  };

  const countDate = getValidCountDate();

  useEffect(() => {
    const timeOut = setTimeout(() => window.scrollTo(0, 0), 0);
    return () => clearTimeout(timeOut);
  }, []);

  ReactGA.initialize(END_POINTS.TRACKING_ID);
  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/verify-email",
      title: "Verify Email Code Page",
    });
  }, []);

  useEffect(() => {
    if (!userId) {
      localStorage.removeItem(EMAIL_VERIFICATION_STORAGE_KEY);
      setTimeout(() => {
        navigate(`${APP_PREFIX_PATH}/login`, { replace: true });
      }, 1000);
    }
  }, [userId, navigate]);

  const handleClose = () => setOpen(false);
  const handleOpenError = () => setOpenError(true);
  const handleCloseError = () => setOpenError(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoadingBtn(true);
    const codeValue = code.trim();
    const requestBody = {
      userId,
      code: codeValue,
    };
    try {
      const res = await fetch.post(END_POINTS.EMAIL_VERIFICATION, requestBody);
      if (res?.state && res?.isConfirmed) {
        setVerified(true);
        setMessage(intl.formatMessage({ id: "success-auth" }));
        setOpen(true);
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.removeItem("emailVerificationData");
        }
        localStorage.removeItem(EMAIL_VERIFICATION_STORAGE_KEY);
        setTimeout(() => {
          navigate(`${APP_PREFIX_PATH}/login`, { replace: true });
        }, 1200);
      } else {
        setVerified(false);
      }
    } catch (err) {
      setMessage(
        err.response?.data?.message || intl.formatMessage({ id: "wrong-auth" }),
      );
      handleOpenError();
      setVerified(false);
    } finally {
      setLoadingBtn(false);
    }
  };

  const successActions = [
    {
      label: intl.formatMessage({ id: "go-to-login" }),
      color: "primary",
      onClick: () => navigate(`${APP_PREFIX_PATH}/login`),
    },
  ];

  if (verified === true) {
    return (
      <ContentLoading isLoading={false}>
        <div className="national-center">
          <InnerHeader
            breadcrumb={[
              {
                title: intl.formatMessage({ id: "account-verification" }),
                clickClass: true,
              },
            ]}
            title={intl.formatMessage({ id: "account-verification" })}
          />
          <div className="container py-5">
            <div className="row">
              <div className="col-12">
                <ResultComponent
                  status="success"
                  title={intl.formatMessage({ id: "success-auth" })}
                  extra={successActions}
                />
              </div>
            </div>
          </div>
        </div>
      </ContentLoading>
    );
  }

  if (!userId) {
    return <ContentLoading isLoading={true} />;
  }

  return (
    <>
      <InnerHeader
        breadcrumb={[
          {
            title: intl.formatMessage({ id: "account-verification" }),
            clickClass: true,
          },
        ]}
        title={intl.formatMessage({ id: "account-verification" })}
      />
      <div className="d-flex align-items-center flex-column py-5">
        <Grid item xs={12} sx={{ textAlign: "center", mb: 3 }}>
          <h2 style={{ color: "var(--main-color)" }}>
            <FormattedMessage id="account-verification" />
          </h2>
          <p className="text-muted">
            <FormattedMessage id="enter-verification-code-email" />
          </p>
        </Grid>
        <Box
          sx={{
            border: "1px solid #E8E8E8",
            borderRadius: "0.75rem",
            width: "50%",
            minWidth: "300px",
          }}
          className="shadow"
        >
          <div className="p-5">
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="mb-2 fw-bold" htmlFor="verifyCode">
                  {intl.formatMessage({ id: "codeLabel" })}
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      ![
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                        "Home",
                        "End",
                      ].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(pasted)) {
                      e.preventDefault();
                    }
                  }}
                  className="bg-transparent form-control fw-bold mx-auto py-2"
                  style={{ fontSize: "14px" }}
                  required
                  name="verifyCode"
                  id="verifyCode"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </div>
              <div className="text-center">
                <button type="submit" className="main-btn mx-auto">
                  <span>
                    {loadingBtn ? (
                      <CircularProgress sx={{ color: "#fff" }} size={26} />
                    ) : (
                      intl.formatMessage({ id: "send" })
                    )}
                  </span>
                </button>
              </div>
            </form>
            <div>
              <CountdownTimer
                countDate={countDate}
                storageKey={EMAIL_VERIFICATION_STORAGE_KEY}
                expireRedirectPath={`${APP_PREFIX_PATH}/login`}
                pathCheck="/verify-email"
                enforceMinimumDuration={false}
              />
            </div>
          </div>
        </Box>

        <SnackbarModal
          open={open}
          onClose={handleClose}
          severity="success"
          message={message}
        />
        <SnackbarModal
          open={openError}
          onClose={handleCloseError}
          severity="error"
          message={message}
        />
      </div>
    </>
  );
}
